import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import ProtectedImage from './ProtectedImage';

const ProfileAvatar = ({ profileId, displayName, size = 'md', showName = false, showRole = false, roleId = null }) => {
  const { accessToken, profilePictureVersion } = useAuthStore();
  const [activeImageUuid, setActiveImageUuid] = useState(null);
  const [loading, setLoading] = useState(true);

  // Size classes
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg',
    xl: 'w-16 h-16 text-xl'
  };

  useEffect(() => {
    if (!profileId || !accessToken) {
      setLoading(false);
      return;
    }

    const fetchActiveImage = async () => {
      try {
        const response = await fetch(`/api/profile/gallery/${profileId}`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        const data = await response.json();

        if (data.success && data.gallery.images) {
          const activeImage = data.gallery.images.find(img => img.is_selected);
          if (activeImage) {
            setActiveImageUuid(activeImage.uuid);
          }
        }
      } catch (err) {
        console.error('Failed to fetch profile image:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveImage();
  }, [profileId, accessToken, profilePictureVersion]);

  const getInitial = () => {
    if (!displayName) return '?';
    return displayName.charAt(0).toUpperCase();
  };

  const getRoleLabel = () => {
    if (roleId === 1) return 'Master';
    if (roleId === 2) return 'slave';
    if (roleId === 3) return 'Switch';
    if (roleId === 4) return 'Observer';
    return '';
  };

  return (
    <div className="flex items-center gap-2">
      {/* Avatar */}
      <div className={`${sizeClasses[size]} rounded-full overflow-hidden flex-shrink-0`}>
        {loading ? (
          <div className="w-full h-full bg-gray-300 dark:bg-gray-600 animate-pulse" />
        ) : activeImageUuid ? (
          <ProtectedImage
            uuid={activeImageUuid}
            alt={displayName}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-red-600 flex items-center justify-center text-white font-bold">
            {getInitial()}
          </div>
        )}
      </div>

      {/* Name and role text */}
      {showName && (
        <div className="text-sm">
          <p className="font-medium text-primary">{displayName}</p>
          {showRole && roleId && (
            <p className="text-secondary text-xs">{getRoleLabel()}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileAvatar;
